import type { ReactNode } from "react";
import { Modal } from "./Modal";
import { Notice } from "./Notice";

export type ConfirmImpact = {
  /** Quantidade de registros vinculados por tipo (ex.: `{ procedures: 3 }`). */
  linkedCounts: Record<string, number>;
  /** `false` bloqueia a confirmação. */
  deletable: boolean;
};

export type ConfirmDialogProps = {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  impact?: ConfirmImpact | null;
  /** Rótulos pt-BR para as chaves de `impact.linkedCounts`. */
  impactLabels?: Record<string, string>;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
};

export const BLOCKED_MESSAGE = "Não é possível excluir: existem vínculos.";

export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  danger = false,
  busy = false,
  error,
  impact,
  impactLabels = {},
  onConfirm,
  onCancel,
  children
}: ConfirmDialogProps) {
  const blocked = impact?.deletable === false;
  const entries = Object.entries(impact?.linkedCounts ?? {}).filter(([, count]) => count > 0);

  return (
    <Modal title={title} onClose={onCancel}>
      <div className="confirm-message">{message}</div>
      {entries.length > 0 && (
        <ul className="confirm-impact" aria-label="Registros vinculados">
          {entries.map(([key, count]) => (
            <li key={key}>
              <strong>{count}</strong> {impactLabels[key] ?? key}
            </li>
          ))}
        </ul>
      )}
      {blocked && <Notice warning={BLOCKED_MESSAGE} />}
      <Notice error={error} />
      {children}
      <div className="form-actions">
        <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={danger ? "primary danger" : "primary"}
          onClick={onConfirm}
          disabled={busy || blocked}
          aria-busy={busy}
        >
          {busy ? "Aguarde…" : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
